import clsx from 'clsx'
import React from 'react'
import { Link } from 'react-router'
import { motion } from 'motion/react'
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/shadcn/hover-card'
import { Progress } from '@/components/shadcn/progress'
import { buttonVariants } from '@/components/shadcn/button'
import { cn } from '@/lib/utils'
import { IconV2LinkOutline } from '@icons/IconV2LinkOutline'
import { FormValues } from '../FormComponent/types'
import { useSubmitPageFilters } from '../../AppPage2/Filter/useSubmitPageFilters'
import { TreeNavigatorDataSchema } from './types'

const cardVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      duration: 0.5,
      ease: 'easeOut' as const,
    },
  },
}

type NodeCardProps = {
  node: TreeNavigatorDataSchema['data']['nodes'][number]
  attribute1Key: string
  attribute2Key?: string
  attribute3Key?: string
  isSelected: boolean
  isDimmed: boolean
  progressPercent: number
  markerPosition?: number
  pageFilters?: FormValues
  linkTo?: string
  onClick: () => void
  isRtl: boolean
}

export const NodeCard = React.forwardRef<HTMLDivElement, NodeCardProps>(
  (
    {
      node,
      attribute1Key,
      attribute2Key,
      attribute3Key,
      isSelected,
      isDimmed,
      onClick,
      progressPercent,
      markerPosition,
      pageFilters,
      linkTo,
      isRtl,
    },
    ref
  ) => {
    const { submitFilters } = useSubmitPageFilters()
    const attr = node.attributes ?? {}
    const attribute1Val = (attr[attribute1Key] as string | undefined) ?? ''
    const attribute2Val = attribute2Key ? ((attr[attribute2Key] as string | undefined) ?? '') : ''
    const attribute3Val = attribute3Key ? ((attr[attribute3Key] as string | undefined) ?? '') : ''

    const { hoverMetaData = [], color } = node

    const nodeCard = (
      <motion.div variants={cardVariants} ref={ref} className="cursor-pointer" onClick={onClick}>
        <div
          className={clsx(
            'relative rounded-md border bg-offwhite px-3 py-2 shadow-sm transition-shadow',
            isSelected ? 'border-primary ring-2 ring-primary/30' : 'border-muted-foreground/20',
            isDimmed ? 'opacity-50' : 'opacity-100'
          )}
        >
          <Progress
            className="mb-1 h-3 bg-foreground/5 shadow"
            value={Math.max(0, Math.min(100, progressPercent))}
            indicatorProps={{ style: { backgroundColor: color } }}
            markerPosition={markerPosition}
          />
          <div className="text-sm font-medium leading-tight">{attribute1Val}</div>
          {(attribute2Val || attribute3Val) && (
            <div className="mt-0.5 text-[11px] text-muted-foreground">
              {[attribute2Val, attribute3Val].filter(Boolean).join(' • ')}
            </div>
          )}
          {!isDimmed && linkTo && (
            <div className={clsx('absolute top-1/2 -translate-y-1/2', { '-left-3': !isRtl, '-right-3': isRtl })}>
              <Link
                to={linkTo}
                onClick={(event) => {
                  if (pageFilters) {
                    submitFilters(pageFilters)
                  }
                  event.stopPropagation()
                }}
                className={cn(
                  buttonVariants({ variant: 'outline', size: 'icon' }),
                  'size-[22px] rounded-full border-2 border-primary text-foreground hover:bg-primary hover:text-background'
                )}
              >
                <IconV2LinkOutline className="size-3.5" />
              </Link>
            </div>
          )}
        </div>
      </motion.div>
    )

    if (!hoverMetaData.length) return nodeCard

    return (
      <HoverCard>
        <HoverCardTrigger asChild className="text-foreground">
          {nodeCard}
        </HoverCardTrigger>
        <HoverCardContent className="w-max bg-offwhite">
          {hoverMetaData.map((meta) => (
            <div className="grid grid-cols-2 items-center gap-2 text-xs" key={meta.label}>
              <div className="justify-self-end font-light italic">{meta.label}:</div>
              <div className="justify-self-start">{meta.value}</div>
            </div>
          ))}
        </HoverCardContent>
      </HoverCard>
    )
  }
)

NodeCard.displayName = 'NodeCard'
